import { useState, useEffect } from 'react';

import { BadgeGroup, BadgeCircle } from '@backrs_ui/common-ui';

import {
  LubavSessionType,
  LubavOfficeHoursSubsessionType,
  OFFICE_HOURS_SUBSESSION_IDS,
  DocumentType,
  ProfileItemType,
  SessionSwitchChoice,
} from '@backrs_ui/grow-ui/src/models/models';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { setOfficeHoursSubsessionType } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';

import { Chip, Box, SxProps, Theme, keyframes } from '@mui/material';

export type PromptVariant = 'BADGE' | 'SINGLE_SELECT';

export type PromptOption = Readonly<{
  id: string;
  badgeName?: string;
  title: string;
  sessionId?: LubavSessionType;
  hiddenPrompt?: string;
  requireDocument?: DocumentType[];
  allowSkipDocument?: boolean;
  includeProfileItems?: ProfileItemType[];
  promptButtonId?: string;
  sessionSwitch?: SessionSwitchChoice;
}>;

type PromptButtonsProps = Readonly<{
  onClick: (option: PromptOption) => void;
  isTyping: boolean;
  promptOptions: readonly PromptOption[];
  variant: PromptVariant;
  selectedOptionId?: string | null;
  selectedOptionIds?: readonly string[];
  disabled?: boolean;
}>;

const isOfficeHoursSubsessionType = (
  sessionId: LubavSessionType,
): sessionId is LubavOfficeHoursSubsessionType => {
  return Object.values(OFFICE_HOURS_SUBSESSION_IDS).includes(
    sessionId as LubavOfficeHoursSubsessionType,
  );
};

export const PromptButtons = ({
  onClick,
  isTyping,
  promptOptions,
  variant,
  selectedOptionId: selectedOptionIdProp,
  selectedOptionIds,
  disabled,
}: PromptButtonsProps) => {
  const dispatch = useAppDispatch();
  const [hoveredOptionId, setHoveredOptionId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (promptOptions.length === 0) return;

    setVisibleCount(0);
    const interval = setInterval(() => {
      setVisibleCount((prev) => {
        if (prev >= promptOptions.length - 1) clearInterval(interval);

        return prev + 1;
      });
    }, 10);

    return () => clearInterval(interval);
  }, [promptOptions.length]);

  const handleOptionClick = (option: PromptOption) => {
    if (!isTyping && !disabled) {
      if (option.sessionId && isOfficeHoursSubsessionType(option.sessionId)) {
        dispatch(setOfficeHoursSubsessionType(option.sessionId));
      }

      onClick(option);
    }
  };

  const isOptionSelected = (optionId: string) => {
    if (selectedOptionIds) {
      return selectedOptionIds.includes(optionId);
    }

    return selectedOptionIdProp === optionId;
  };

  if (variant === 'BADGE') {
    return (
      <Box sx={promptButtonsContainer}>
        {promptOptions.slice(0, visibleCount).map((option) => (
          <Box key={option.id} sx={badgeGroupWrapperAnimated}>
            <BadgeGroup
              badgeName={option.badgeName || ''}
              title={option.title}
              isOpen={false}
              setIsOpen={() => {
                handleOptionClick(option);
              }}
              loading={false}
            >
              <Box />
            </BadgeGroup>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={promptButtonsContainer}>
      {promptOptions.slice(0, visibleCount).map((option) => {
        const isSelected = isOptionSelected(option.id);
        const shouldShowBadge = isSelected || hoveredOptionId === option.id;

        return (
          <Box key={option.id} sx={staggeredItemSx}>
            <Chip
              id={`prompt-option-${option.id}`}
              variant="badge"
              label={option.title}
              size="sm"
              icon={
                <Box sx={getBadgeIconSx(shouldShowBadge)}>
                  <BadgeCircle size="md" />
                </Box>
              }
              type="leading"
              onClick={() => handleOptionClick(option)}
              onMouseEnter={() => {
                if (!isSelected) {
                  setHoveredOptionId(option.id);
                }
              }}
              onMouseLeave={() => {
                if (!isSelected) {
                  setHoveredOptionId(null);
                }
              }}
              sx={getChipSx(isSelected)}
            />
          </Box>
        );
      })}
    </Box>
  );
};

const promptButtonsContainer: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  pt: backrs.spacing['spacing-lg'],
  width: '100%',
  boxSizing: 'border-box',
  maxWidth: '100%',
});

const badgeGroupWrapper: SxProps<Theme> = {
  maxWidth: '100%',
  minWidth: 0,
  flex: '0 1 auto',
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const badgeGroupWrapperAnimated: SxProps<Theme> = {
  ...badgeGroupWrapper,
  animation: `${fadeIn} 0.3s ease-out forwards`,
};

const getBadgeIconSx = (shouldShowBadge: boolean): SxProps<Theme> => ({
  opacity: shouldShowBadge ? 1 : 0,
  transform: shouldShowBadge ? 'scale(1)' : 'scale(0.8)',
  maxWidth: shouldShowBadge ? '20px' : '0px',
  overflow: 'hidden',
  transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out, max-width 0.2s ease-in-out',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
});

const getChipSx =
  (isSelected: boolean): SxProps<Theme> =>
  ({ backrs, typography }) => ({
    cursor: 'pointer',
    width: 'fit-content',
    maxWidth: '100%',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    transition: 'width 0.2s ease-in-out, border-color 0.2s ease-in-out',
    ...(isSelected && {
      borderColor: backrs.colors['Brand/500'],
    }),
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '& .MuiChip-icon': {
      marginLeft: backrs.spacing['spacing-none'],
      marginRight: backrs.spacing['spacing-none'],
      alignSelf: 'flex-start',
      marginTop: backrs.spacing['spacing-xs'],
    },
    '& .MuiChip-label': {
      marginLeft: undefined,
      transition: 'margin-left 0.2s ease-in-out',
      ...typography['text-md-regular'],
      textAlign: 'left',
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      maxWidth: '100%',
    },
  });

const staggeredItemSx: SxProps<Theme> = {
  animation: `${fadeIn} 0.3s ease-out forwards`,
};
